// Third-party
import React from 'react';
import classNames from 'classnames';

// Components
import NoDataCell from './NoDataCell';
import IntegerValue from '../common/IntegerValue';

// Utils
import { getCountryName } from '../../../utils/i18n';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import NoDataText from '@/types/no-data-text';

type Props = {
  value: {
    name: string;
    weight: number | null;
    symbol?: string;
    category?: string;
  };
  className?: string;
  item?: ComparisonItem;
  disclaimer?: string;
  onClick?: () => void;
  id?: string;
  displaySymbol?: boolean;
  locale?: Locale;
};

const HoldingCell: React.FunctionComponent<Props> = ({ value, className, item, disclaimer, onClick, id, displaySymbol, locale }: Props) => {
  if (value == null || value.weight == null) {
    // If there is a disclaimer that could explain the missing data, show it.
    if (disclaimer) {
      return <NoDataCell item={item} disclaimerKey={disclaimer} onClick={onClick} id={id} locale={locale} />;
    }

    // Else, display a dash - there might simply be not enough items to display, even if all data is available.
    return (
      <NoDataCell
        className={classNames(className, 'cell holding-cell not-applicable center-align-dash')}
        onClick={onClick}
        id={id}
        locale={locale}
        noDataText={NoDataText.Dash}
      />
    );
  }
  let label = '';
  if (displaySymbol && value.symbol) {
    label = value.symbol;
  } else if (value.category && value.category === 'countryAllocations') {
    label = getCountryName(value.name);
  } else {
    label = value.name;
  }

  return (
    <div
      className={classNames(className, 'cell holding-cell')}
      onClick={onClick}
      id={id ? `cell-${id}` : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="markup-container">
        <div className="value" role="presentation">
          <IntegerValue value={value.weight} isPercentage locale={locale} />
        </div>
        <div className="label" role="presentation">
          <span className="holding-label">{label}</span>
        </div>
      </div>
    </div>
  );
};

export default HoldingCell;
